<template>
  <div class="chat-container fill-height" role="main" aria-label="چت پشتیبانی">
    <!-- Skip to latest message link for screen readers -->
<!--    <a href="#messagesArea" class="skip-link" @click="scrollToBottom">پرش به آخرین پیام</a>-->
    
    <div class="chat-wrapper fill-height">
      <div class="d-flex flex-column fill-height">
        <!-- Header: RTL – name on left (info), avatar on right -->
        <header class="chat-header" role="banner" :aria-label="headerAriaLabel">
          <div class="chat-header__inner">
            <button
              v-if="$vuetify.breakpoint.smAndDown"
              class="chat-header__back"
              @click="$router.push({name: 'dashboard', params: {lang: 'fa'}})"
              :title="'بازگشت'"
              aria-label="بازگشت"
            >
              <v-icon size="24" color="#FFFFFF">mdi-arrow-right</v-icon>
            </button>

            <div class="chat-header__main">
              <div class="chat-header__avatar-wrap">
                <div
                  class="chat-header__avatar"
                  role="img"
                  :aria-label="accountManager && accountManager.full_name ? `آواتار ${accountManager.full_name}` : 'پشتیبان'"
                >
                  <img
                    v-if="accountManager && accountManager.avatar && !avatarLoadError"
                    :src="accountManager.avatar"
                    :alt="accountManager.full_name ? `${accountManager.full_name}، پشتیبان` : 'پشتیبان'"
                    class="chat-header__avatar-img"
                    @error="onAvatarError"
                  />
                  <span v-else-if="accountManager && accountManager.full_name" class="chat-header__avatar-initials" aria-hidden="true">{{ agentInitials }}</span>
                  <v-icon v-else color="#FFFFFF" size="28" aria-hidden="true">mdi-headset</v-icon>
                </div>
                <span
                  v-if="hasAgentStatus"
                  class="chat-header__status"
                  :class="{ 'chat-header__status--online': accountManager && accountManager.online === true }"
                  :aria-label="accountManager && accountManager.online === true ? 'آنلاین' : 'آفلاین'"
                />
              </div>
              <div class="chat-header__info">
                <h1 class="chat-header__title" :title="accountManager && accountManager.full_name ? accountManager.full_name : ''">
                  <template v-if="accountManager && accountManager.full_name">{{ accountManager.full_name }}</template>
                  <template v-else>{{ $t('dashboard.chat.noManager') || 'در حال حاضر پشتیبان مشخص نشده است' }}</template>
                </h1>
                <p v-if="headerSubtitleText" class="chat-header__subtitle">{{ headerSubtitleText }}</p>
              </div>
            </div>

            <div class="chat-header__actions">
              <ChatControls
                :messages="normalizedMessages"
                @search="handleSearch"
                @clear-chat="handleClearChat"
                @download-transcript="handleDownloadTranscript"
              />
            </div>
          </div>
        </header>

        <!-- Connection Status -->
        <ConnectionStatus 
          :status="connectionStatus"
          @retry="handleReconnect"
        />

        <!-- Messages Area -->
        <div 
          class="messages-container" 
          id="messagesArea" 
          ref="messagesArea"
          role="log"
          aria-live="polite"
          aria-label="پیام‌های گفتگو"
          tabindex="0"
          @keydown="handleMessagesAreaKeydown"
          @scroll="handleScroll"
        >
          <!-- Loading indicator for history -->
          <div v-if="isLoadingHistory" class="history-loader" role="status" aria-live="polite">
            <v-progress-circular indeterminate size="24" width="2" color="#5B7CFA"></v-progress-circular>
            <span>در حال بارگذاری پیام‌های قدیمی...</span>
          </div>
          <!-- Skeleton Loading -->
          <div v-if="localLoader && normalizedMessages.length === 0" class="skeleton-container">
            <MessageSkeleton v-for="n in 3" :key="n" :isUser="n % 2 === 0" />
          </div>
          
          <!-- Empty State -->
          <div v-else-if="normalizedMessages.length === 0 && !localLoader" class="empty-state">
            <div class="empty-state-illustration">
              <div class="empty-state-icon-wrapper">
                <v-icon color="#667eea" size="80">mdi-chat-processing-outline</v-icon>
              </div>
            </div>
            <h3 class="empty-state-title">سلام! چطور می‌تونیم کمکتون کنیم؟</h3>
            <p class="empty-state-text">تیم پشتیبانی ما آماده پاسخگویی به سوالات شماست</p>
            <div class="empty-state-hours">
              <v-icon size="16" color="#9E9E9E">mdi-clock-outline</v-icon>
              <span>ساعات پاسخگویی: حداکثر تا 24 ساعت آینده</span>
            </div>
          </div>
          
          <div class="messages-list">
            <div
              v-for="message in groupedMessages"
              :key="message.id"
              class="message-row"
              :class="{ 'message-row--grouped': message.isGrouped }"
            >
              <!-- Date Separator -->
              <DateSeparator 
                v-if="message.showDateSeparator"
                :key="`date-${message.id}`"
                :date="message.ts"
              />
              
              <!-- Message Item -->
              <article 
                :key="message.id"
                class="message-item"
                :data-message-id="message.id"
                :class="{
                  'search-highlight': searchResults.some(r => r.id === message.id),
                  'message-item--failed': message.status === 'failed' || message._failed
                }"
                :aria-label="`پیام از ${message.is_mine ? 'شما' : 'پشتیبانی'}`"
                role="article"
              >
                <!-- File message renderer (secure blob-based rendering) -->
                <div
                  v-if="message.file && (message.type === 'file' || message.type === null || !message.type)"
                  class="file-message-row"
                  :class="{ 'file-message-row--mine': message.is_mine }"
                >
                  <div
                    class="file-message"
                    :class="{ 'file-message--mine': message.is_mine }"
                  >
                    <!-- Image preview (using secure blob URL) -->
                    <div
                      v-if="isImageFileMessage(message)"
                      class="file-message__image-wrapper"
                    >
                      <div v-if="getFileLoadingState(message.file)" class="file-message__loading">
                        <v-progress-circular indeterminate size="32" width="3" color="#5B7CFA"></v-progress-circular>
                        <span>در حال بارگذاری تصویر...</span>
                      </div>
                      <div v-else-if="getFileErrorState(message.file)" class="file-message__error-state">
                        <v-icon color="#F44336" size="24">mdi-alert-circle</v-icon>
                        <span>خطا در بارگذاری تصویر</span>
                        <v-btn
                          small
                          outlined
                          color="#5B7CFA"
                          @click="retryFileLoad(message.file)"
                        >
                          تلاش مجدد
                        </v-btn>
                      </div>
                      <a
                        v-else
                        class="file-message__link"
                        :href="getFileBlobUrlSync(message) || '#'"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.prevent="handleFileClick(message)"
                      >
                        <img
                          v-if="getFileBlobUrlSync(message)"
                          class="file-message__image"
                          :src="getFileBlobUrlSync(message)"
                          :alt="getFileName(message)"
                          loading="lazy"
                          @load="handleFileImageLoad(message)"
                          @error="handleFileImageError(message)"
                        />
                        <div v-else class="file-message__image-placeholder">
                          <v-progress-circular indeterminate size="24" width="2" color="#5B7CFA"></v-progress-circular>
                        </div>
                      </a>
                    </div>
                    
                    <!-- Show text below image -->
                    <div v-if="isImageFileMessage(message) && getMessageText(message)" class="file-message__caption file-message__caption--below" v-html="formatMessageWithLinks(getMessageText(message))">
                    </div>

                    <!-- Video player (using secure blob URL) -->
                    <div
                      v-else-if="isVideoFileMessage(message)"
                      class="file-message__video"
                    >
                      <div v-if="getFileLoadingState(message.file)" class="file-message__loading">
                        <v-progress-circular indeterminate size="32" width="3" color="#5B7CFA"></v-progress-circular>
                        <span>در حال بارگذاری ویدیو...</span>
                      </div>
                      <div v-else-if="getFileErrorState(message.file)" class="file-message__error-state">
                        <v-icon color="#F44336" size="24">mdi-alert-circle</v-icon>
                        <span>خطا در بارگذاری ویدیو</span>
                        <v-btn
                          small
                          outlined
                          color="#5B7CFA"
                          @click="retryFileLoad(message.file)"
                        >
                          تلاش مجدد
                        </v-btn>
                      </div>
                      <video
                        v-else-if="getFileBlobUrlSync(message)"
                        controls
                        :src="getFileBlobUrlSync(message)"
                        class="file-message__video-player"
                        preload="metadata"
                        @error="handleFileVideoError(message, $event)"
                      >
                        مرورگر شما از پخش فایل ویدیویی پشتیبانی نمی‌کند.
                      </video>
                      <div v-else class="file-message__loading">
                        <v-progress-circular indeterminate size="32" width="3" color="#5B7CFA"></v-progress-circular>
                        <span>در حال بارگذاری ویدیو...</span>
                      </div>
                    </div>
                    
                    <!-- Show text below video -->
                    <div v-if="isVideoFileMessage(message) && getMessageText(message)" class="file-message__caption file-message__caption--below" v-html="formatMessageWithLinks(getMessageText(message))">
                    </div>

                    <!-- Audio player: custom minimal UI (play, progress, time, mute) -->
                    <div
                      v-else-if="isAudioFileMessage(message)"
                      class="file-message__audio"
                    >
                      <div v-if="getFileLoadingState(message.file)" class="file-message__loading">
                        <v-progress-circular indeterminate size="32" width="3" color="#5B7CFA"></v-progress-circular>
                        <span>در حال بارگذاری فایل صوتی...</span>
                      </div>
                      <div v-else-if="getFileErrorState(message.file)" class="file-message__error-state">
                        <v-icon color="#F44336" size="24">mdi-alert-circle</v-icon>
                        <span>خطا در بارگذاری فایل صوتی</span>
                      </div>
                      <template v-else-if="getFileBlobUrlSync(message)">
                        <audio
                          :ref="el => setAudioRef(getMessageId(message), el)"
                          :src="getFileBlobUrlSync(message)"
                          preload="metadata"
                          @play="onAudioPlay(message)"
                          @pause="onAudioPause(message)"
                          @timeupdate="onAudioTimeUpdate(message, $event)"
                          @loadedmetadata="onAudioLoadedMetadata(message, $event)"
                          @ended="onAudioEnded(message)"
                          @error="handleFileAudioError(message, $event)"
                        >
                          مرورگر شما از پخش فایل صوتی پشتیبانی نمی‌کند.
                        </audio>
                        <div class="audio-player">
                          <button
                            type="button"
                            class="audio-player__btn"
                            :aria-label="getAudioState(message).isPlaying ? 'توقف' : 'پخش'"
                            @click="playPauseAudio(message)"
                          >
                            <v-icon size="22">{{ getAudioState(message).isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                          </button>
                          <div
                            class="audio-player__progress-wrap"
                            role="button"
                            tabindex="0"
                            :aria-label="`موقعیت: ${formatAudioTime(getAudioState(message).currentTime)} از ${formatAudioTime(getAudioState(message).duration)}`"
                            @click="seekAudio(message, $event)"
                          >
                            <div class="audio-player__progress" :style="{ width: progressPercent(message) + '%' }"></div>
                          </div>
                          <div class="audio-player__time">
                            <span>{{ formatAudioTime(getAudioState(message).currentTime) }}</span>
                            <span class="audio-player__time-sep">/</span>
                            <span>{{ formatAudioTime(getAudioState(message).duration) }}</span>
                          </div>
                          <button
                            type="button"
                            class="audio-player__volume"
                            :aria-label="getAudioState(message).muted ? 'صدای بلند' : 'بی‌صدا'"
                            @click="toggleAudioMute(message)"
                          >
                            <v-icon size="18">{{ getAudioState(message).muted ? 'mdi-volume-mute' : 'mdi-volume-high' }}</v-icon>
                          </button>
                        </div>
                      </template>
                      <div v-else class="file-message__loading">
                        <v-progress-circular indeterminate size="32" width="3" color="#5B7CFA"></v-progress-circular>
                        <span>در حال بارگذاری فایل صوتی...</span>
                      </div>
                    </div>
                    
                    <!-- Show text below audio -->
                    <div v-if="isAudioFileMessage(message) && getMessageText(message)" class="file-message__caption file-message__caption--below" v-html="formatMessageWithLinks(getMessageText(message))">
                    </div>

                    <!-- Attachment card: other file types only (PDF, doc, generic). Image/video use thumbnail/player + minimal row below. -->
                    <div
                      v-else-if="!isImageFileMessage(message) && !isVideoFileMessage(message) && !isAudioFileMessage(message)"
                      class="file-attachment-card"
                    >
                      <div class="file-attachment-card__head">
                        <v-icon class="file-attachment-card__icon" size="28">{{ getAttachmentIcon(message) }}</v-icon>
                        <div class="file-attachment-card__info">
                          <span class="file-attachment-card__name" :title="getFileName(message)">{{ getFileNameTruncated(message) }}</span>
                          <span v-if="formatFileSize(message.file && message.file.size)" class="file-attachment-card__size">{{ formatFileSize(message.file.size) }}</span>
                        </div>
                      </div>
                      <div class="file-attachment-card__actions">
                        <button
                          type="button"
                          class="file-attachment-card__download"
                          :disabled="getFileLoadingState(message.file)"
                          :aria-label="getFileLoadingState(message.file) ? 'در حال بارگذاری' : `دانلود فایل: ${getFileName(message)}`"
                          @click="handleFileDownload(message)"
                        >
                          <v-icon size="18">mdi-download</v-icon>
                          <span>{{ getFileLoadingState(message.file) ? 'در حال بارگذاری...' : 'دانلود فایل' }}</span>
                        </button>
                      </div>
                    </div>
                    
                    <!-- Show text below other file types -->
                    <div v-if="!isImageFileMessage(message) && !isVideoFileMessage(message) && !isAudioFileMessage(message) && getMessageText(message)" class="file-message__caption file-message__caption--below" v-html="formatMessageWithLinks(getMessageText(message))">
                    </div>

                    <div v-if="message.status === 'failed' || message._failed" class="file-message__error">
                      ارسال فایل ناموفق بود. دوباره تلاش کنید.
                    </div>

                    <!-- Download row for image/video (thumbnail/player shown above) -->
                    <div v-if="isImageFileMessage(message) || isVideoFileMessage(message)" class="file-attachment-card file-attachment-card--minimal">
                      <div class="file-attachment-card__info">
                        <span class="file-attachment-card__name" :title="getFileName(message)">{{ getFileNameTruncated(message) }}</span>
                        <span v-if="formatFileSize(message.file && message.file.size)" class="file-attachment-card__size">{{ formatFileSize(message.file.size) }}</span>
                      </div>
                      <button
                        type="button"
                        class="file-attachment-card__download"
                        :disabled="getFileLoadingState(message.file)"
                        :aria-label="getFileLoadingState(message.file) ? 'در حال بارگذاری' : `دانلود فایل: ${getFileName(message)}`"
                        @click="handleFileDownload(message)"
                      >
                        <v-icon size="18">mdi-download</v-icon>
                        <span>{{ getFileLoadingState(message.file) ? 'در حال بارگذاری...' : 'دانلود فایل' }}</span>
                      </button>
                    </div>

                    <div v-if="message.showTimestamp" class="file-message__timestamp">
                      {{ message.date }}
                    </div>
                  </div>
                </div>

                <!-- Default (text) message renderer -->
                <TextMessages
                  v-else
                  :message="{
                    contents: message.contents,
                    date: message.date,
                    status: message.status || 'sent',
                    id: message.id,
                    type: message.type || 'text',
                    media: message.media,
                    ts: message.ts
                  }"
                  :IsMeSend="message.is_mine"
                  :isBookmarked="bookmarkedMessages.includes(message.id)"
                  :showAvatar="message.showAvatar"
                  :isGrouped="message.isGrouped"
                  :showTimestamp="message.showTimestamp"
                  :isRecent="isRecentMessage(message.ts)"
                  @bookmark="handleBookmark"
                  @edit="handleEditMessage"
                  @delete="handleDeleteMessage"
                  @retry="handleRetryMessage"
                />
              </article>
            </div>
          </div>
        </div>

        <!-- Quick Replies Section (سوالات متداول) - hidden for now -->
        <!--
        <QuickReplies
          :showQuickReplies="showQuickReplies"
          :contextReplies="contextReplies"
          :isSending="localLoader"
          @send="handleQuickReply"
          @toggle="toggleQuickReplies"
        />
        -->

        <!-- Enhanced Input Area -->
        <EnhancedMessageInput
          v-model="textMessage"
          :isSending="localLoader"
          :supportTyping="supportIsTyping"
          :placeholder="$t('dashboard.chat.placeholder') || 'چیزی بنویسید...'"
          @send="handleSendMessage"
          @input="handleTyping"
          @error="handleInputError"
        />
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification
      :visible="toastVisible"
      :type="toastType"
      :message="toastMessage"
      @close="closeToast"
    />
  </div>
</template>

<script>
/*eslint-disable*/
import TextMessages from "@/components/Messeges/TextMessages";
import QuickReplies from "@/components/Messeges/QuickReplies";
import ChatControls from "@/components/Messeges/ChatControls";
import EnhancedMessageInput from "@/components/Messeges/EnhancedMessageInput";
import DateSeparator from "@/components/Messeges/DateSeparator";
import MessageSkeleton from "@/components/Messeges/MessageSkeleton";
import ConnectionStatus from "@/components/Messeges/ConnectionStatus";
import ToastNotification from "@/components/Messeges/ToastNotification";
import moment from "moment-jalaali";

export default {
  components: {
    TextMessages,
    QuickReplies,
    ChatControls,
    EnhancedMessageInput,
    DateSeparator,
    MessageSkeleton,
    ConnectionStatus,
    ToastNotification,
  },
  data() {
    return {
      rid: null,
      accountManager: null, // { id, full_name, avatar } from thread API, or null when {}
      messages: [],
      textMessage: null,
      localLoader: false,
      pollingInterval: null,
      lastMessageTs: null,
      // TODO: Unread tracking - localStorage key for last seen timestamp
      lastSeenTsStorageKey: 'clientChat_lastSeenTs',
      showQuickReplies: false,
      contextReplies: [],
      lastSupportMessage: null,
      isOnline: true, // TODO: Get from API
      averageResponseTime: null, // Will be calculated from messages
      searchResults: [],
      bookmarkedMessages: [],
      supportIsTyping: false,
      typingTimeout: null,
      typingCheckInterval: null,
      connectionStatus: 'connected', // connected, connecting, disconnected, offline
      failedMessages: [], // Messages that failed to send
      toastVisible: false,
      toastMessage: '',
      toastType: 'success',
      connectionCheckInterval: null,
      isLoadingHistory: false,
      hasMoreHistory: true,
      scrollPosition: null,
      isLoadingMessages: false, // Prevent concurrent loadMessages calls
      lastLoadTime: 0, // Track last load time to prevent rapid calls
      isReconnecting: false, // Prevent multiple reconnection attempts
      scrollDebounceTimer: null, // Debounce scroll events
      isRestoringScroll: false, // Prevent scroll events during restoration
      // Legacy: Presigned URL resolution for protected /file-upload links (deprecated, using download_url now)
      signedUrlInFlight: Object.create(null), // fileId -> Promise
      previousMessageCount: 0, // Store previous message count to detect new messages
      // Secure file blob URL cache
      // IMPORTANT: We cannot use <img src="download_url"> directly because:
      // - The app uses Bearer token authentication (Authorization header)
      // - Browsers cannot send custom headers (like Authorization) with <img src> or <audio src>
      // - Solution: Fetch file as blob with Authorization header, then create object URL
      // - This allows secure file rendering while maintaining authentication
      fileBlobUrlById: {}, // fileId -> blob URL string
      fileLoadingById: {}, // fileId -> boolean
      fileErrorById: {}, // fileId -> error message
      fileBlobFetchInFlight: Object.create(null), // fileId -> Promise
      avatarLoadError: false, // fallback to initials when avatar image fails
      // Custom audio player: refs and state per message
      audioRefs: {},
      audioStateByMessageId: {},
    };
  },
  computed: {
    /** Initials for avatar fallback (e.g. "آیلین عابدین" → "آع") */
    agentInitials() {
      const name = this.accountManager && this.accountManager.full_name;
      if (!name || typeof name !== 'string') return '';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).trim();
      return parts[0].charAt(0) || '';
    },
    /** Optional subtitle below name; extend accountManager with subtitle/role when backend provides it */
    headerSubtitleText() {
      const am = this.accountManager;
      if (!am) return '';
      if (am.role) return am.role;
      if (am.subtitle) return am.subtitle;
      if (am.full_name) return 'پشتیبانی';
      return '';
    },
    /** Show status dot only when backend provides online/status */
    hasAgentStatus() {
      const am = this.accountManager;
      return am && ('online' in am || (am.status !== undefined && am.status !== null));
    },
    /** Accessible label for chat header (dynamic agent name) */
    headerAriaLabel() {
      const name = this.accountManager && this.accountManager.full_name;
      return name ? `چت پشتیبانی با ${name}` : 'چت پشتیبانی';
    },
    normalizedMessages() {
      const normalized = this.messages
        .map(msg => this.normalizeMessage(msg))
        .sort((a, b) => {
          // Prioritize temp messages (user's just sent messages) to be at the end
          if (a._temp && !b._temp) return 1; // Temp message goes after
          if (!a._temp && b._temp) return -1; // Temp message goes after
          // Otherwise sort by timestamp
          return a.ts - b.ts;
        });
      
      // Ensure the most recent message is always at the end
      // This ensures user's sent messages appear at the end
      if (normalized.length > 0) {
        // Find the message with the highest timestamp
        const mostRecentIndex = normalized.reduce((maxIndex, current, index) => {
          // Prioritize temp messages
          if (current._temp && !normalized[maxIndex]._temp) return index;
          if (!current._temp && normalized[maxIndex]._temp) return maxIndex;
          // Otherwise compare timestamps
          return current.ts > normalized[maxIndex].ts ? index : maxIndex;
        }, 0);
        
        // If the most recent message is not at the end, move it there
        if (mostRecentIndex !== normalized.length - 1) {
          const mostRecent = normalized.splice(mostRecentIndex, 1)[0];
          normalized.push(mostRecent);
        }
      }
      
      return normalized;
    },
    groupedMessages() {
      if (!this.normalizedMessages || this.normalizedMessages.length === 0) {
        return [];
      }

      const grouped = [];
      let lastDate = null;
      const GROUP_TIME_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes – normal messages
      const SHORT_GROUP_TIME_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes – short messages (e.g. "test", "hi")
      const SHORT_MESSAGE_MAX_LENGTH = 50; // chars – treat as short for tighter grouping

      const getMessageContentLength = (m) => {
        if (!m) return 0;
        const text = m.contents || m.text || m.msg || m.message || (m.file && m.file.name) || '';
        return typeof text === 'string' ? text.trim().length : 0;
      };
      const isShortMessage = (m) => getMessageContentLength(m) <= SHORT_MESSAGE_MAX_LENGTH;

      this.normalizedMessages.forEach((msg, index) => {
        const msgDate = new Date(msg.ts);
        const msgDateStr = msgDate.toDateString();
        const prevMsg = index > 0 ? this.normalizedMessages[index - 1] : null;
        const nextMsg = index < this.normalizedMessages.length - 1 ? this.normalizedMessages[index + 1] : null;

        // Check if we need a date separator
        const showDateSeparator = lastDate !== msgDateStr;
        if (showDateSeparator) {
          lastDate = msgDateStr;
        }

        // Group with previous: same sender + within time window (longer window for short messages)
        const timeSincePrev = prevMsg ? msg.ts - prevMsg.ts : Infinity;
        const bothShort = prevMsg && isShortMessage(prevMsg) && isShortMessage(msg);
        const groupTimeThreshold = bothShort ? SHORT_GROUP_TIME_THRESHOLD_MS : GROUP_TIME_THRESHOLD_MS;
        const isGrouped = prevMsg &&
          prevMsg.is_mine === msg.is_mine &&
          timeSincePrev < groupTimeThreshold;

        // Show avatar only for first message in support group or when sender changes
        const timeSincePrevForAvatar = prevMsg ? msg.ts - prevMsg.ts : Infinity;
        const showAvatar = !msg.is_mine && (
          !prevMsg ||
          prevMsg.is_mine !== msg.is_mine ||
          timeSincePrevForAvatar >= groupTimeThreshold
        );

        // Show timestamp on last message in group or when time gap is large
        const timeUntilNext = nextMsg ? nextMsg.ts - msg.ts : Infinity;
        const nextGroupThreshold = nextMsg && isShortMessage(msg) && isShortMessage(nextMsg)
          ? SHORT_GROUP_TIME_THRESHOLD_MS
          : GROUP_TIME_THRESHOLD_MS;
        const showTimestamp = !nextMsg ||
          nextMsg.is_mine !== msg.is_mine ||
          timeUntilNext >= nextGroupThreshold ||
          index === this.normalizedMessages.length - 1;

        grouped.push({
          ...msg,
          showDateSeparator,
          isGrouped,
          showAvatar,
          showTimestamp
        });
      });

      return grouped;
    },
    // TODO: Unread tracking - compute unread count from messages with ts > lastSeenTs
    unreadCount() {
      const lastSeenTs = this.getLastSeenTs();
      if (!lastSeenTs) return 0;
      
      return this.normalizedMessages.filter(msg => {
        // Only count messages that are not from the current user
        return !msg.is_mine && msg.ts > lastSeenTs;
      }).length;
    },
  },
  methods: {
    async loadMessages({ initial = false, loadHistory = false } = {}) {
      if (!this.rid) return;

      // Prevent concurrent calls - debounce non-initial loads
      if (!initial && !loadHistory) {
        const now = Date.now();
        const timeSinceLastLoad = now - this.lastLoadTime;
        // Prevent calls more frequent than every 5 seconds
        if (timeSinceLastLoad < 5000) {
          return;
        }
      }

      // Prevent concurrent API calls
      if (this.isLoadingMessages && !initial) {
        return;
      }

      this.isLoadingMessages = true;
      this.lastLoadTime = Date.now();

      // Store current message count before API call
      const currentMessageCount = this.messages.length;


      // Determine if loading history (scroll up) or new messages
      // Convert timestamps to ISO format for API
      const beforeTs = loadHistory ? this.formatTimestampForAPI(this.getOldestMessageTs()) : '';
      const afterTs = loadHistory ? '' : this.formatTimestampForAPI(this.lastMessageTs);
      
      const params = {
        limit: 50,
        before: beforeTs,
        after: afterTs,
      };

      try {
        // Save scroll position before loading history
        if (loadHistory) {
          this.saveScrollPosition();
        }

        this.isLoadingHistory = loadHistory;
        const resp = await this.requesting('clientChat', 'messages', params);
        
        if (resp && resp.data) {
          let newMessages = [];
          
          // Handle API response format A: { messages: [...] }
          if (resp.data.messages && Array.isArray(resp.data.messages)) {
            newMessages = resp.data.messages;
          }
          // Handle API response format B: raw Rocket.Chat messages (array directly)
          else if (Array.isArray(resp.data)) {
            newMessages = resp.data;
          }
          // Handle Rocket.Chat format with result array
          else if (resp.data.result && Array.isArray(resp.data.result)) {
            newMessages = resp.data.result;
          }

          // Filter out system messages (user joins, etc.)
          const validMessages = newMessages.filter(msg => {
            // Exclude user join notifications and other system messages
            return msg.type !== 'uj' && msg.type !== 'ul' && msg.type !== 'ru';
          });

          // Remove all temp messages before adding API messages
          // Clean up blob URLs from temp messages
          this.messages = this.messages.filter(m => {
            if (m._temp) {
              // Clean up blob URLs if they exist
              if (m.file && typeof m.file === 'object') {
                if (m.file.download_url && m.file.download_url.startsWith('blob:')) {
                  try {
                    URL.revokeObjectURL(m.file.download_url);
                  } catch (e) {
                    // Ignore errors
                  }
                }
                if (m.file.image_url && m.file.image_url.startsWith('blob:')) {
                  try {
                    URL.revokeObjectURL(m.file.image_url);
                  } catch (e) {
                    // Ignore errors
                  }
                }
                if (m.file.audio_url && m.file.audio_url.startsWith('blob:')) {
                  try {
                    URL.revokeObjectURL(m.file.audio_url);
                  } catch (e) {
                    // Ignore errors
                  }
                }
              }
              // Remove temp message
              return false;
            }
            // Keep non-temp messages
            return true;
          });

          // Merge messages without duplicates
          // Also update existing messages if they have empty text but API has text
          const existingIds = new Set(this.messages.map(m => this.getMessageId(m)));
          const messagesToAdd = [];
          
          validMessages.forEach(msg => {
            const msgId = this.getMessageId(msg);
            if (!existingIds.has(msgId)) {
              // New message - add it
              messagesToAdd.push(msg);
              existingIds.add(msgId);
            } else {
              // Message already exists - check if we need to update text
              const existingIndex = this.messages.findIndex(m => this.getMessageId(m) === msgId);
              if (existingIndex !== -1) {
                const existingMsg = this.messages[existingIndex];
                // If existing message has empty text but new message from API has text, update it
                const existingText = existingMsg.text || existingMsg.contents || existingMsg.msg || existingMsg.message || '';
                const newText = msg.text || msg.contents || msg.msg || msg.message || '';
                
                // Update if: existing has no text OR existing text is empty/whitespace, AND new has text
                if ((!existingText || !existingText.trim()) && newText && newText.trim()) {
                  // Update the existing message with text from API
                  // Use spread to merge, but prioritize new message data (especially text)
                  this.messages[existingIndex] = {
                    ...existingMsg,
                    ...msg,
                    // Preserve any local fields that might be important
                    _temp: existingMsg._temp,
                    status: existingMsg.status || msg.status,
                  };
                } else if (newText && newText.trim() && existingText !== newText) {
                  // Even if existing has text, if API has different (non-empty) text, update it
                  // This handles cases where text was updated on the server
                  this.messages[existingIndex] = {
                    ...existingMsg,
                    ...msg,
                    _temp: existingMsg._temp,
                    status: existingMsg.status || msg.status,
                  };
                }
              }
            }
          });

          if (loadHistory) {
            if (messagesToAdd.length > 0) {
              // Prepend older messages
              this.messages = [...messagesToAdd, ...this.messages];
              // Update previous count (don't scroll for history)
              this.previousMessageCount = this.messages.length;
              // Check if there's more history - only true if we got a full page (50 messages)
              // Use original newMessages length to account for filtered system messages
              this.hasMoreHistory = newMessages.length === 50;
              
              // Restore scroll position after loading history
              this.$nextTick(() => {
                this.isRestoringScroll = true;
                this.restoreScrollPosition();
                // Reset flag after a short delay
                setTimeout(() => {
                  this.isRestoringScroll = false;
                }, 200);
              });
            } else {
              // No more messages returned - we've reached the beginning
              this.hasMoreHistory = false;
            }
          } else {
            // Append new messages - ensure they're added at the end
            if (messagesToAdd.length > 0) {
              // Sort messages by timestamp (oldest first, newest last)
              messagesToAdd.sort((a, b) => {
                const tsA = a.ts ? (typeof a.ts === 'string' ? new Date(a.ts).getTime() : a.ts) : 0;
                const tsB = b.ts ? (typeof b.ts === 'string' ? new Date(b.ts).getTime() : b.ts) : 0;
                return tsA - tsB;
              });
              
              // Add new messages to the end
              this.messages.push(...messagesToAdd);
              
              // Sort all messages by timestamp to ensure correct chronological order
              this.messages.sort((a, b) => {
                const tsA = a.ts ? (typeof a.ts === 'string' ? new Date(a.ts).getTime() : a.ts) : 0;
                const tsB = b.ts ? (typeof b.ts === 'string' ? new Date(b.ts).getTime() : b.ts) : 0;
                return tsA - tsB;
              });
              
              // Always ensure the most recent message (by timestamp) is at the end
              // This ensures user's sent messages appear at the end
              if (this.messages.length > 0) {
                // Find the message with the highest timestamp
                const mostRecentMessage = this.messages.reduce((latest, current) => {
                  const latestTs = latest.ts ? (typeof latest.ts === 'string' ? new Date(latest.ts).getTime() : latest.ts) : 0;
                  const currentTs = current.ts ? (typeof current.ts === 'string' ? new Date(current.ts).getTime() : current.ts) : 0;
                  return currentTs > latestTs ? current : latest;
                });
                
                // Move the most recent message to the end if it's not already there
                const mostRecentId = this.getMessageId(mostRecentMessage);
                const currentLastId = this.messages.length > 0 ? this.getMessageId(this.messages[this.messages.length - 1]) : null;
                
                if (mostRecentId !== currentLastId) {
                  const index = this.messages.findIndex(m => this.getMessageId(m) === mostRecentId);
                  if (index !== -1 && index !== this.messages.length - 1) {
                    this.messages.splice(index, 1);
                    this.messages.push(mostRecentMessage);
                  }
                }
              }
            }
            
            // Check if new messages were added and scroll to bottom
            const newMessageCount = this.messages.length;
            if (newMessageCount > this.previousMessageCount && !initial) {
              // New messages detected (not initial load), scroll to bottom
              this.$nextTick(() => {
                this.scrollToBottom();
              });
            }
            // Update previous count
            this.previousMessageCount = newMessageCount;
            
            // After adding new messages, check if support has responded to mark client messages as read
            this.$nextTick(() => {
              this.updateMessageStatuses();
            });
          }

          
          // Update previous message count on initial load
          if (initial && !loadHistory) {
            this.previousMessageCount = this.messages.length;
          }

          // Update lastMessageTs for incremental updates
          if (newMessages.length > 0) {
            const timestamps = newMessages.map(m => this.getMessageTimestamp(m)).filter(ts => ts);
            if (timestamps.length > 0) {
              const maxNewTs = Math.max(...timestamps);
              // Update lastMessageTs to the maximum of current and new messages
              this.lastMessageTs = this.lastMessageTs ? Math.max(this.lastMessageTs, maxNewTs) : maxNewTs;
            }
          }

          if (initial && !loadHistory) {
            this.$nextTick(() => {
              this.scrollToBottom();
              // Update message statuses on initial load
              this.updateMessageStatuses();
            });
            // Calculate average response time on initial load
            this.$nextTick(() => {
              this.calculateAverageResponseTime();
            });
          }

          this.isLoadingHistory = false;
          this.isLoadingMessages = false;

          // Update context-based suggestions based on last support message
          this.updateContextReplies(newMessages);
          
          // Recalculate average response time when new messages arrive
          if (newMessages.length > 0) {
            this.$nextTick(() => {
              this.calculateAverageResponseTime();
              // Announce new messages to screen readers
              newMessages.forEach(msg => {
                const normalized = this.normalizeMessage(msg);
                if (!normalized.is_mine) {
                  this.announceNewMessage(normalized);
                }
              });
              // Preload blob URLs for file messages (lazy load)
              this.preloadFileBlobUrls(newMessages);
            });
          }

          // TODO: Unread tracking - update lastSeenTs when messages are loaded and route is active
          if (this.$route.name === 'support-chat') {
            this.updateLastSeenTs();
          }
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        this.isLoadingHistory = false;
        this.isLoadingMessages = false;
      }
    },

    getOldestMessageTs() {
      if (this.messages.length === 0) return null;
      const timestamps = this.messages.map(m => this.getMessageTimestamp(m)).filter(ts => ts && ts > 0);
      if (timestamps.length === 0) return null;
      const oldest = Math.min(...timestamps);
      // Validate timestamp is reasonable (not just a year)
      if (oldest < 1000000000000) { // Less than year 2001 in milliseconds
        return null;
      }
      return oldest;
    },

    saveScrollPosition() {
      const messagesArea = this.$refs.messagesArea;
      if (messagesArea) {
        this.scrollPosition = {
          scrollTop: messagesArea.scrollTop,
          scrollHeight: messagesArea.scrollHeight
        };
      }
    },

    restoreScrollPosition() {
      const messagesArea = this.$refs.messagesArea;
      if (messagesArea && this.scrollPosition) {
        // Temporarily disable scroll handler
        this.isRestoringScroll = true;
        
        const heightDiff = messagesArea.scrollHeight - this.scrollPosition.scrollHeight;
        const targetScrollTop = this.scrollPosition.scrollTop + heightDiff;
        
        messagesArea.scrollTop = targetScrollTop;
        this.scrollPosition = null;
        
        // Re-enable scroll handler after restoration
        setTimeout(() => {
          this.isRestoringScroll = false;
        }, 200);
      }
    },

    handleScroll() {
      // Ignore scroll events during restoration
      if (this.isRestoringScroll) return;

      const messagesArea = this.$refs.messagesArea;
      if (!messagesArea || this.isLoadingHistory || !this.hasMoreHistory || this.isLoadingMessages) {
        return;
      }

      // Clear existing debounce timer
      if (this.scrollDebounceTimer) {
        clearTimeout(this.scrollDebounceTimer);
      }

      // Debounce scroll handler - wait 300ms after scrolling stops
      this.scrollDebounceTimer = setTimeout(() => {
        // Double-check conditions after debounce
        if (this.isLoadingHistory || !this.hasMoreHistory || this.isLoadingMessages) {
          return;
        }

        // Load more when scrolled near top (within 300px)
        if (messagesArea.scrollTop < 300) {
          this.loadMessages({ initial: false, loadHistory: true });
        }
      }, 300);
    },

    handleSendMessage(messageData) {
      const { text, file } = messageData;
      this.sendMessage(text, file);
    },

    handleInputError(errorMessage) {
      this.showToast(errorMessage, 'error');
    },

    async sendMessage(messageText, file = null) {
      if ((!messageText || !messageText.trim()) && !file) {
        return;
      }

      const text = messageText ? messageText.trim() : '';
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      // Use a timestamp that's guaranteed to be the latest (current time + small offset to ensure it's always last)
      const tempTs = Date.now() + 1; // Add 1ms to ensure it's always the latest

      // Optimistic update - add temporary message
      const optimisticMessage = {
        id: tempId,
        text: text || (file ? `[فایل: ${file.name}]` : ''),
        ts: tempTs,
        sender_type: 'client',
        status: 'sending',
        _temp: true,
        file: file,
      };
      // Always add user's sent message at the end
      this.messages.push(optimisticMessage);
      // Ensure it's at the end (in case of any sorting issues)
      const index = this.messages.findIndex(m => m.id === tempId);
      if (index !== -1 && index !== this.messages.length - 1) {
        this.messages.splice(index, 1);
        this.messages.push(optimisticMessage);
      }
      this.textMessage = '';

      this.$nextTick(() => {
        this.scrollToBottom();
      });

        try {
        // Check connection status
        if (this.connectionStatus === 'offline' || this.connectionStatus === 'disconnected') {
          throw new Error('No connection');
        }

        // Use HTTP API for sending messages
        let payload = { text: text };
        let requestHeaders = {};
        let endpoint = 'send';

        // Use upload endpoint for file uploads
        if (file) {
          const formData = new FormData();
          // Use 'message' field for optional text (as per API spec)
          if (text) {
            formData.append('message', text);
          }
          formData.append('file', file);
          payload = formData;
          endpoint = 'upload';
          // Don't set Content-Type header - browser will set it with boundary for FormData
        }

        const resp = await this.requesting('clientChat', endpoint, {}, payload, requestHeaders);

        // Handle response - check for upload endpoint success format
        if (resp && resp.data) {
          // For upload endpoint, check if status is SUCCESS
          if (endpoint === 'upload') {
            if (resp.status === 'SUCCESS' || (resp.data && resp.data.success)) {
              // Upload successful - loadMessages will remove temp messages and add new ones from API
              await this.loadMessages({ initial: false });
              this.$nextTick(() => {
                this.scrollToBottom();
                
                // Upgrade newly sent messages to 'delivered' after a delay
                const newlySentMessages = this.messages.filter(m => {
                  const normalized = this.normalizeMessage(m);
                  return normalized.is_mine && (m.status === 'sent' || !m.status);
                });
                
                newlySentMessages.forEach(msg => {
                  setTimeout(() => {
                    if (msg.status === 'sent' || !msg.status) {
                      msg.status = 'delivered';
                      msg._status = 'delivered';
                      this.$forceUpdate();
                    }
                  }, 2000); // 2 seconds delay
                });
              });
              
              // Haptic feedback on mobile
              this.vibrate();
            } else {
              // Upload failed - throw error to be caught below
              throw new Error(resp.message || 'آپلود فایل ناموفق بود');
            }
          } else {
            // Regular message send endpoint - loadMessages will remove temp messages and add new ones
            await this.loadMessages({ initial: false });
            this.$nextTick(() => {
              this.scrollToBottom();
              
              // Upgrade newly sent messages to 'delivered' after a delay
              const newlySentMessages = this.messages.filter(m => {
                const normalized = this.normalizeMessage(m);
                return normalized.is_mine && (m.status === 'sent' || !m.status);
              });
              
              newlySentMessages.forEach(msg => {
                setTimeout(() => {
                  if (msg.status === 'sent' || !msg.status) {
                    msg.status = 'delivered';
                    msg._status = 'delivered';
                    this.$forceUpdate();
                  }
                }, 2000); // 2 seconds delay
              });
            });
            
            // Haptic feedback on mobile
            this.vibrate();
          }
        }
      } catch (error) {
        console.error('Error sending message:', error);
        
        // Extract error message from API response if available
        let errorMessage = 'خطا در ارسال';
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.data && errorData.data.errors) {
            // Handle validation errors
            const errors = errorData.data.errors;
            const firstError = Object.values(errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = firstError[0];
            } else if (typeof firstError === 'string') {
              errorMessage = firstError;
            }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Rollback optimistic update - mark as failed
        const optimisticIndex = this.messages.findIndex(m => m.id === tempId);
        if (optimisticIndex > -1) {
          const failedMessage = {
            ...this.messages[optimisticIndex],
            status: 'failed',
            _failed: true,
            _retryData: { text, file },
            _error: errorMessage
          };
          this.messages[optimisticIndex] = failedMessage;
          this.failedMessages.push(failedMessage);
          
          // Show error toast with specific error message
          const toastMessage = file 
            ? `آپلود فایل ناموفق: ${errorMessage}` 
            : `پیام ارسال نشد: ${errorMessage}`;
          this.showToast(toastMessage, 'error');
        }
        
        this.updateConnectionStatus('disconnected');
      }
    },

    handleTyping() {
      // Typing indicator is handled via polling
      // Clear existing timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }

      // Set timeout to stop typing indicator after 3 seconds of inactivity
      this.typingTimeout = setTimeout(() => {
        // Typing indicator cleared via polling
      }, 3000);
    },

    checkSupportTyping() {
      // Check if support is typing by looking at recent message timestamps
      // Uses polling to check typing status
      const recentSupportMessages = this.normalizedMessages
        .filter(msg => !msg.is_mine)
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 1);

      if (recentSupportMessages.length > 0) {
        const lastSupportMsg = recentSupportMessages[0];
        const timeSinceLastMsg = Date.now() - lastSupportMsg.ts;
        // If last message was less than 5 seconds ago, assume support might be typing
        this.supportIsTyping = timeSinceLastMsg < 5000 && timeSinceLastMsg > 0;
      } else {
        this.supportIsTyping = false;
      }
    },

    normalizeMessage(m) {
      let id, contents, ts, is_mine, date, status, type, file;
      // Handle format A: { id, text, ts, sender_type, sender_name, type }
      // This matches the API response structure: { id, text, ts, sender_type, type, file }
      if (m.id && m.text !== undefined) {
        id = m.id;
        // Extract text from all possible fields - prioritize text field from API
        // m.text can be empty string "", but if it's defined, use it (even if empty)
        // Only check other fields if m.text is null or undefined
        if (m.text !== undefined && m.text !== null) {
          contents = m.text; // Use m.text even if it's empty string
        } else {
          contents = m.contents || m.msg || m.message || '';
        }
        // Convert ISO timestamp string to milliseconds
        if (typeof m.ts === 'string') {
          ts = new Date(m.ts).getTime();
        } else {
          ts = m.ts || (m._updatedAt ? new Date(m._updatedAt.$date).getTime() : Date.now());
        }
        is_mine = m.sender_type === 'client' || m.alias !== undefined;
        type = m.type || (m.file ? 'file' : null);
        file = m.file || null;
        
        // Check for explicit status first, then check for read-related fields
        if (m.status) {
          status = m.status;
        } else if (m.read_at || m.readAt || m.read_by || m.readBy) {
          status = 'read';
        } else if (m.delivered_at || m.deliveredAt) {
          status = 'delivered';
        } else if (is_mine) {
          // For client messages, check if we have a stored status, otherwise default to 'sent'
          // We'll upgrade this to 'delivered' after a delay and 'read' when support responds
          status = m._status || 'sent';
        } else {
          status = null;
        }
      }
      // Handle format B: Rocket.Chat message format
      else if (m._id) {
        id = m._id;
        // Extract text from all possible fields
        contents = m.msg || m.text || m.contents || m.message || '';
        ts = m.ts ? (typeof m.ts === 'object' && m.ts.$date ? new Date(m.ts.$date).getTime() : (typeof m.ts === 'number' ? m.ts : parseInt(m.ts))) : Date.now();
        // Check if message is from client (check u.username, u.name, or alias)
        is_mine = (m.u && m.u.username === 'client') || m.alias !== undefined;
        type = m.t || null;
        file = m.file || null;
        // Rocket.Chat status: t = temporary (sending), sent = sent, read = read
        if (m.t) {
          status = 'sending';
        } else if (m.readBy && Array.isArray(m.readBy) && m.readBy.length > 0) {
          status = 'read';
        } else if (is_mine) {
          status = 'sent';
        } else {
          status = null;
        }
      }
      // Fallback
      else {
        id = m.id || `msg_${Date.now()}`;
        // Extract text from all possible fields
        contents = m.text || m.contents || m.msg || m.message || '';
        ts = m.ts || Date.now();
        is_mine = m.sender_type === 'client' || m.is_mine || false;
        status = m.status || (is_mine ? 'sent' : null);
        type = m.type || (m.file ? 'file' : null);
        file = m.file || null;
      }

      // Format date using moment-jalaali
      date = moment(new Date(ts)).format('jYYYY/jMM/jDD HH:mm');

      // Preserve file object with download_url (new backend format)
      // Ensure file object is properly structured with download_url, file_id, file_name, file_type
      if (file && typeof file === 'object') {
        // For file messages, ensure contents is populated from all possible text fields
        // The text field from API should already be in contents, but double-check to be safe
        // If contents is empty or whitespace, check all possible text fields again
        if (!contents || (typeof contents === 'string' && !contents.trim())) {
          const fileContents = (m.text !== undefined && m.text !== null) ? m.text :
                              m.contents || m.msg || m.message || 
                              (m.file && m.file.message) || '';
          if (fileContents && typeof fileContents === 'string' && fileContents.trim()) {
            contents = fileContents;
          }
        }
        // Preserve all file properties, especially download_url from new backend
        file = {
          ...file,
          // Ensure we have the new backend fields
          file_id: file.file_id || file._id || file.id,
          download_url: file.download_url || null,
          file_name: file.file_name || file.name || file.original_name || file.filename,
          file_type: file.file_type || file.type || file.mime || file.contentType,
        };
      }

      return {
        id,
        contents,
        ts,
        is_mine,
        date,
        status,
        type,
        file,
        // Preserve temp flag for sorting
        _temp: m._temp || false,
      };
    },

    /** True if text looks like a file label (should not be shown as caption to avoid duplicate with attachment card). */
    isFileLabelText(text) {
      if (!text || typeof text !== 'string') return false;
      const t = text.trim();
      return (
        t.startsWith('[فایل:') ||
        t.startsWith('[File:') ||
        t.startsWith('File: ') ||
        t.startsWith('فایل: ')
      );
    },

    getMessageText(message) {
      const check = (str) => {
        if (!str || typeof str !== 'string') return '';
        const trimmed = str.trim();
        if (!trimmed || this.isFileLabelText(trimmed)) return '';
        return trimmed;
      };
      const fromMessage = check(message.contents) || check(message.text) || check(message.msg) || check(message.message);
      if (fromMessage) return fromMessage;

      const originalMessage = this.messages.find(m => {
        const normalized = this.normalizeMessage(m);
        return normalized.id === message.id;
      });
      if (originalMessage) {
        const text =
          originalMessage.text ||
          originalMessage.msg ||
          originalMessage.contents ||
          originalMessage.message ||
          (originalMessage.file && originalMessage.file.message) ||
          '';
        const out = check(text);
        if (out) return out;
      }
      return '';
    },

    /**
     * Converts URLs in message text to clickable links
     * @param {string} text - The message text
     * @returns {string} - HTML string with links
     */
    formatMessageWithLinks(text) {
      if (!text || typeof text !== 'string') return '';
      
      // Escape HTML to prevent XSS
      const escapeHtml = (str) => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      };
      
      // URL regex pattern - matches http, https, www, and common domains
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
      
      // Split text by URLs and convert URLs to links
      const parts = [];
      let lastIndex = 0;
      let match;
      
      while ((match = urlRegex.exec(text)) !== null) {
        // Add text before the URL
        if (match.index > lastIndex) {
          parts.push(escapeHtml(text.substring(lastIndex, match.index)));
        }
        
        // Process the URL
        let url = match[0];
        let href = url;
        
        // Add protocol if missing
        if (!url.match(/^https?:\/\//i)) {
          href = 'https://' + url;
        }
        
        // Remove trailing slash from display URL (but keep it in href)
        let displayUrl = url;
        if (displayUrl.endsWith('/')) {
          displayUrl = displayUrl.slice(0, -1);
        }
        
        // Escape the URL for display but keep href functional
        displayUrl = escapeHtml(displayUrl);
        parts.push(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="message-link" style="color: #4CAF50 !important; text-decoration: underline;">${displayUrl}</a>`);
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(escapeHtml(text.substring(lastIndex)));
      }
      
      // If no URLs found, return escaped text
      if (parts.length === 0) {
        return escapeHtml(text);
      }
      
      return parts.join('');
    },

    isImageFileMessage(message) {
      // Use file_type from new backend format (e.g., "image/jpeg")
      const fileType = message?.file?.file_type;
      if (fileType && typeof fileType === 'string' && fileType.startsWith('image/')) {
        return true;
      }
      // Fallback: check filename extension
      const fileName = this.getFileName(message);
      if (fileName) {
        return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(fileName);
      }
      // Optimistic upload (File object)
      if (typeof File !== 'undefined' && message?.file instanceof File) {
        return message.file.type?.startsWith('image/');
      }
      return false;
    },

    isAudioFileMessage(message) {
      // Use file_type from new backend format (e.g., "audio/mpeg")
      const fileType = message?.file?.file_type;
      if (fileType && typeof fileType === 'string' && fileType.startsWith('audio/')) {
        return true;
      }
      // Fallback: check filename extension
      const fileName = this.getFileName(message);
      if (fileName) {
        return /\.(mp3|wav|ogg|m4a|aac|flac|wma)(\?|#|$)/i.test(fileName);
      }
      // Optimistic upload (File object)
      if (typeof File !== 'undefined' && message?.file instanceof File) {
        return message.file.type?.startsWith('audio/');
      }
      return false;
    },

    isVideoFileMessage(message) {
      // Use file_type from new backend format (e.g., "video/mp4")
      const fileType = message?.file?.file_type;
      if (fileType && typeof fileType === 'string' && fileType.startsWith('video/')) {
        return true;
      }
      // Fallback: check filename extension
      const fileName = this.getFileName(message);
      if (fileName) {
        return /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv)(\?|#|$)/i.test(fileName);
      }
      // Optimistic upload (File object)
      if (typeof File !== 'undefined' && message?.file instanceof File) {
        return message.file.type?.startsWith('video/');
      }
      return false;
    },

    resolveFileUrl(url) {
      if (!url || typeof url !== 'string') return '';
      // Already absolute (S3 presigned, etc.)
      if (/^https?:\/\//i.test(url)) return url;
      // Make relative URLs absolute using API base url
      const base = (process.env.VUE_APP_BASE_URL || '').replace(/\/+$/, '');
      if (!base) return url;
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${base}${path}`;
    },

    /**
     * Extract file ID from legacy Rocket.Chat URL format (deprecated).
     * Only used as fallback for old messages. New messages use file.file_id directly.
     */
    extractFileIdFromUrl(url) {
      if (!url || typeof url !== 'string') return null;
      // Legacy pattern: /file-upload/{fileId}/ (Rocket.Chat format, deprecated)
      const m = url.match(/\/file-upload\/([^/]+)\//);
      return m && m[1] ? m[1] : null;
    },

    getBackendFileId(message) {
      // Prefer new backend file_id field
      if (message?.file?.file_id) return message.file.file_id;
      // Fallback to legacy fields
      return (
        message?.file?._id ||
        message?.file?.id ||
        this.extractFileIdFromUrl(message?.file?.file_url) ||
        this.extractFileIdFromUrl(message?.file?.image_url) ||
        null
      );
    },

    /**
     * Fetches file as blob with Authorization header and creates object URL.
     * Required because <img src> cannot send Authorization header for Bearer token auth.
     * Caches blob URLs by file_id to avoid refetching on re-render.
     */
    async ensureBlobUrl(file) {
      if (!file || !file.download_url) {
        return null;
      }

      const fileId = file.file_id;
      if (!fileId) {
        console.warn('ensureBlobUrl: file missing file_id');
        return null;
      }

      // Return cached blob URL if available
      if (this.fileBlobUrlById[fileId]) {
        return this.fileBlobUrlById[fileId];
      }

      // Return in-flight promise if already fetching
      if (this.fileBlobFetchInFlight[fileId]) {
        return await this.fileBlobFetchInFlight[fileId];
      }

      // Start fetching
      this.$set(this.fileLoadingById, fileId, true);
      this.$delete(this.fileErrorById, fileId);

      const fetchPromise = (async () => {
        try {
          // Resolve download_url to absolute URL if needed
          let downloadUrl = file.download_url;
          if (!/^https?:\/\//i.test(downloadUrl)) {
            const base = (process.env.VUE_APP_BASE_URL || '').replace(/\/+$/, '');
            downloadUrl = downloadUrl.startsWith('/') 
              ? `${base}${downloadUrl}` 
              : `${base}/${downloadUrl}`;
          }

          // Fetch with Authorization header
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('No authentication token available');
          }

          const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          // Cache the blob URL
          this.$set(this.fileBlobUrlById, fileId, blobUrl);
          this.$set(this.fileLoadingById, fileId, false);

          return blobUrl;
        } catch (error) {
          console.error('Error fetching file blob:', error);
          this.$set(this.fileErrorById, fileId, error.message || 'Failed to load file');
          this.$set(this.fileLoadingById, fileId, false);
          return null;
        } finally {
          delete this.fileBlobFetchInFlight[fileId];
        }
      })();

      this.fileBlobFetchInFlight[fileId] = fetchPromise;
      return await fetchPromise;
    },

    async ensureSignedUrl(message) {
      try {
        const fileId = this.getBackendFileId(message);
        const name = this.getFileName(message);
        if (!fileId || !name) return null;

        if (message?.file?.signed_url) return message.file.signed_url;

        // De-dupe in-flight requests per fileId
        if (this.signedUrlInFlight[fileId]) {
          return await this.signedUrlInFlight[fileId];
        }

        const p = (async () => {
          const resp = await this.requesting('clientChat', 'signedUrl', { fileId, name });
          const data = resp?.data?.data || resp?.data || {};
          const url = data.url || data.signed_url || data.file_url || null;
          const expiresIn = data.expires_in || data.expiresIn || null;
          if (!url) throw new Error(resp?.data?.message || 'Failed to get signed url');

          if (message?.file && typeof message.file === 'object') {
            this.$set(message.file, 'signed_url', url);
            if (expiresIn) this.$set(message.file, 'signed_expires_in', expiresIn);
          }

          return url;
        })().finally(() => {
          delete this.signedUrlInFlight[fileId];
        });

        this.signedUrlInFlight[fileId] = p;
        return await p;
      } catch (e) {
        return null;
      }
    },

    getFileName(message) {
      // Backend file object
      const name =
        message?.file?.file_name ||
        message?.file?.name ||
        message?.file?.original_name ||
        message?.file?.filename;
      if (name) return name;
      // Optimistic upload (File object)
      if (typeof File !== 'undefined' && message?.file instanceof File) {
        return message.file.name;
      }
      return 'Download file';
    },

    /** Truncate filename for display (ellipsis after 40 chars). */
    getFileNameTruncated(message) {
      const name = this.getFileName(message);
      if (!name) return '';
      return name.length > 40 ? name.slice(0, 40) + '…' : name;
    },

    /** Icon for attachment card by type (PDF, image, generic). No new deps – Vuetify icons only. */
    getAttachmentIcon(message) {
      const type = message?.file?.file_type || message?.file?.type || '';
      const name = this.getFileName(message) || '';
      if (type.includes('pdf') || /\.pdf(\?|#|$)/i.test(name)) return 'mdi-file-pdf-box';
      if (type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?|#|$)/i.test(name)) return 'mdi-file-image';
      return 'mdi-file-outline';
    },

    /** Format file size for display only when already in data. No computation/fetch. */
    formatFileSize(bytes) {
      if (bytes == null || typeof bytes !== 'number' || bytes < 0) return '';
      if (bytes === 0) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    /**
     * Get blob URL for file rendering (images, audio, video).
     * Uses secure blob fetching with Authorization header.
     */
    async getFileBlobUrl(message) {
      if (!message?.file) return null;

      // For optimistic uploads (File object), create object URL directly
      if (typeof File !== 'undefined' && message.file instanceof File) {
        return URL.createObjectURL(message.file);
      }

      // Use new backend download_url with blob fetching
      return await this.ensureBlobUrl(message.file);
    },

    /**
     * Get URL for file download (creates blob URL if needed).
     * Used for download links and buttons.
     */
    async getFileDownloadUrl(message) {
      if (!message?.file) return '#';

      // For optimistic uploads (File object), create object URL directly
      if (typeof File !== 'undefined' && message.file instanceof File) {
        return URL.createObjectURL(message.file);
      }

      // Use blob URL for secure download
      const blobUrl = await this.ensureBlobUrl(message.file);
      return blobUrl || '#';
    },

    /**
     * Legacy method - kept for compatibility but now uses blob URLs.
     * @deprecated Use getFileBlobUrl() instead
     */
    getFileUrl(message) {
      // Return cached blob URL if available (synchronous access)
      if (message?.file?.file_id && this.fileBlobUrlById[message.file.file_id]) {
        return this.fileBlobUrlById[message.file.file_id];
      }
      // For optimistic uploads
      if (typeof File !== 'undefined' && message?.file instanceof File) {
        return URL.createObjectURL(message.file);
      }
      return '#';
    },

    /**
     * Get blob URL for image rendering.
     * Uses secure blob fetching with Authorization header.
     */
    async getImageUrl(message) {
      if (!this.isImageFileMessage(message)) return '';
      return await this.getFileBlobUrl(message);
    },

    /**
     * Get blob URL for audio rendering.
     * Uses secure blob fetching with Authorization header.
     */
    async getAudioUrl(message) {
      if (!this.isAudioFileMessage(message)) return '';
      return await this.getFileBlobUrl(message);
    },

    /**
     * Get blob URL for video rendering.
     * Uses secure blob fetching with Authorization header.
     */
    async getVideoUrl(message) {
      if (!this.isVideoFileMessage(message)) return '';
      return await this.getFileBlobUrl(message);
    },

    /**
     * Get cached blob URL synchronously (for template binding).
     * Returns empty string if not yet loaded.
     * Triggers loading if file has download_url but blob URL is not cached.
     */
    getFileBlobUrlSync(message) {
      if (!message?.file?.file_id) return '';
      
      const fileId = message.file.file_id;
      const cachedUrl = this.fileBlobUrlById[fileId];
      
      // If cached, return it
      if (cachedUrl) return cachedUrl;
      
      // If not cached and has download_url, trigger loading (async, won't block render)
      if (message.file.download_url && !this.fileLoadingById[fileId] && !this.fileBlobFetchInFlight[fileId]) {
        this.ensureBlobUrl(message.file).catch(err => {
          console.warn('Error loading file blob:', err);
        });
      }
      
      return '';
    },

    /**
     * Check if file is currently loading.
     */
    getFileLoadingState(file) {
      if (!file?.file_id) return false;
      return this.fileLoadingById[file.file_id] || false;
    },

    /**
     * Get error message for file if loading failed.
     */
    getFileErrorState(file) {
      if (!file?.file_id) return null;
      return this.fileErrorById[file.file_id] || null;
    },

    /**
     * Retry loading a file that failed.
     */
    async retryFileLoad(file) {
      if (!file?.file_id) return;
      // Clear error and retry
      this.$delete(this.fileErrorById, file.file_id);
      // If blob URL exists, revoke it first
      if (this.fileBlobUrlById[file.file_id]) {
        URL.revokeObjectURL(this.fileBlobUrlById[file.file_id]);
        this.$delete(this.fileBlobUrlById, file.file_id);
      }
      // Trigger reload
      await this.ensureBlobUrl(file);
    },

    /**
     * Handle file click (open in new tab or download).
     */
    async handleFileClick(message) {
      const blobUrl = await this.getFileBlobUrl(message);
      if (blobUrl) {
        window.open(blobUrl, '_blank');
      }
    },

    /**
     * Handle file download.
     */
    async handleFileDownload(message) {
      if (!message?.file) return;

      try {
        let blobUrl = this.getFileBlobUrlSync(message);
        
        // If not cached, fetch it
        if (!blobUrl) {
          blobUrl = await this.ensureBlobUrl(message.file);
        }

        if (!blobUrl) {
          this.showToast('خطا در دانلود فایل', 'error');
          return;
        }

        // Fetch the blob to create download link
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getFileName(message);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.showToast('فایل با موفقیت دانلود شد', 'success');
      } catch (error) {
        console.error('Error downloading file:', error);
        this.showToast('خطا در دانلود فایل', 'error');
      }
    },

    /**
     * Handle successful image load.
     */
    handleFileImageLoad(message) {
      // Image loaded successfully
      if (message?.file?.file_id) {
        this.$delete(this.fileErrorById, message.file.file_id);
      }
    },

    /**
     * Handle image load error.
     */
    handleFileImageError(message) {
      // Try to reload the blob URL
      if (message?.file?.file_id) {
        const fileId = message.file.file_id;
        // Clear cached blob URL and retry
        if (this.fileBlobUrlById[fileId]) {
          URL.revokeObjectURL(this.fileBlobUrlById[fileId]);
          this.$delete(this.fileBlobUrlById, fileId);
        }
        // Retry loading
        this.ensureBlobUrl(message.file).then(blobUrl => {
          if (blobUrl) {
            this.$forceUpdate();
          }
        });
      }
    },

    /**
     * Handle video load error.
     */
    handleFileVideoError(message, event) {
      console.warn('Failed to load video file:', message?.file?.file_id);
      if (message?.file?.file_id) {
        const fileId = message.file.file_id;
        // Clear cached blob URL and retry
        if (this.fileBlobUrlById[fileId]) {
          URL.revokeObjectURL(this.fileBlobUrlById[fileId]);
          this.$delete(this.fileBlobUrlById, fileId);
        }
        // Retry loading
        this.ensureBlobUrl(message.file).then(blobUrl => {
          if (blobUrl && event && event.target) {
            event.target.src = blobUrl;
            this.$forceUpdate();
          }
        });
      }
    },

    setAudioRef(id, el) {
      if (el) this.audioRefs[id] = el;
    },

    getAudioState(message) {
      const id = this.getMessageId(message);
      return this.audioStateByMessageId[id] || { isPlaying: false, currentTime: 0, duration: 0, muted: false };
    },

    playPauseAudio(message) {
      const id = this.getMessageId(message);
      const el = this.audioRefs[id];
      if (!el) return;
      if (this.getAudioState(message).isPlaying) el.pause();
      else el.play();
    },

    onAudioPlay(message) {
      const id = this.getMessageId(message);
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), isPlaying: true });
    },

    onAudioPause(message) {
      const id = this.getMessageId(message);
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), isPlaying: false });
    },

    onAudioTimeUpdate(message, e) {
      const id = this.getMessageId(message);
      const currentTime = e.target.currentTime;
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), currentTime });
    },

    onAudioLoadedMetadata(message, e) {
      const id = this.getMessageId(message);
      const duration = e.target.duration;
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), duration });
    },

    onAudioEnded(message) {
      const id = this.getMessageId(message);
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), isPlaying: false, currentTime: 0 });
    },

    seekAudio(message, e) {
      const id = this.getMessageId(message);
      const el = this.audioRefs[id];
      if (!el || !el.duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = this.$vuetify && this.$vuetify.rtl ? rect.right - e.clientX : e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      el.currentTime = pct * el.duration;
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), currentTime: el.currentTime });
    },

    progressPercent(message) {
      const s = this.getAudioState(message);
      if (!s.duration || s.duration <= 0) return 0;
      return (s.currentTime / s.duration) * 100;
    },

    formatAudioTime(sec) {
      if (sec === undefined || sec === null || isNaN(sec)) return '0:00';
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      return `${m}:${s < 10 ? '0' : ''}${s}`;
    },

    toggleAudioMute(message) {
      const id = this.getMessageId(message);
      const el = this.audioRefs[id];
      if (!el) return;
      el.muted = !el.muted;
      this.$set(this.audioStateByMessageId, id, { ...this.getAudioState(message), muted: el.muted });
    },

    /**
     * Handle audio load error.
     */
    handleFileAudioError(message, event) {
      console.warn('Failed to load audio file:', message?.file?.file_id);
      if (message?.file?.file_id) {
        const fileId = message.file.file_id;
        // Clear cached blob URL and retry
        if (this.fileBlobUrlById[fileId]) {
          URL.revokeObjectURL(this.fileBlobUrlById[fileId]);
          this.$delete(this.fileBlobUrlById, fileId);
        }
        // Retry loading
        this.ensureBlobUrl(message.file).then(blobUrl => {
          if (blobUrl && event && event.target) {
            event.target.src = blobUrl;
            this.$forceUpdate();
          }
        });
      }
    },

    /**
     * Preload blob URLs for file messages (lazy loading).
     * Only loads files that are images, audio, or video (not other file types).
     */
    preloadFileBlobUrls(messages) {
      messages.forEach(msg => {
        const normalized = this.normalizeMessage(msg);
        if (normalized.file && normalized.file.download_url && normalized.file.file_id) {
          // Only preload media files (images, audio, video) for immediate rendering
          const fileType = normalized.file.file_type;
          if (fileType && (
            fileType.startsWith('image/') ||
            fileType.startsWith('audio/') ||
            fileType.startsWith('video/')
          )) {
            // Trigger blob URL loading (will be cached)
            this.ensureBlobUrl(normalized.file).catch(err => {
              console.warn('Failed to preload file blob:', err);
            });
          }
        }
      });
    },

    getMessageId(msg) {
      return msg.id || msg._id || `msg_${JSON.stringify(msg)}`;
    },

    onAvatarError() {
      this.avatarLoadError = true;
    },

    /** Set accountManager from thread API data. Treats {} or missing id as null. */
    setAccountManagerFromResponse(data) {
      const am = data && data.account_manager;
      if (am && am.id) {
        this.accountManager = {
          id: am.id,
          full_name: am.full_name || '',
          avatar: am.avatar || null
        };
      } else {
        this.accountManager = null;
      }
    },

    // Mark all client messages as 'read' when support responds
    markClientMessagesAsRead() {
      const normalized = this.normalizedMessages;
      let lastSupportMessageTs = null;
      
      // Find the timestamp of the last support message
      for (let i = normalized.length - 1; i >= 0; i--) {
        if (!normalized[i].is_mine) {
          lastSupportMessageTs = normalized[i].ts;
          break;
        }
      }
      
      // Mark all client messages before the last support message as 'read'
      if (lastSupportMessageTs) {
        this.messages.forEach(msg => {
          const normalized = this.normalizeMessage(msg);
          if (normalized.is_mine && normalized.ts < lastSupportMessageTs) {
            // Only upgrade status, don't downgrade
            if (!msg.status || (msg.status !== 'read' && msg.status !== 'failed')) {
              msg.status = 'read';
              msg._status = 'read'; // Store for persistence
            }
          }
        });
      }
    },

    // Update message statuses: upgrade 'sent' to 'delivered' after delay, and mark as 'read' when support responds
    updateMessageStatuses() {
      const now = Date.now();
      
      this.messages.forEach(msg => {
        const normalized = this.normalizeMessage(msg);
        
        // Only process client messages
        if (!normalized.is_mine) return;
        
        // Skip if already failed or read
        if (msg.status === 'failed' || msg.status === 'read') return;
        
        // Upgrade 'sent' to 'delivered' after 2 seconds
        if (msg.status === 'sent' || !msg.status) {
          const messageAge = now - normalized.ts;
          if (messageAge > 2000) { // 2 seconds
            msg.status = 'delivered';
            msg._status = 'delivered';
          } else {
            // Schedule upgrade after delay
            setTimeout(() => {
              if (msg.status === 'sent' || !msg.status) {
                msg.status = 'delivered';
                msg._status = 'delivered';
                this.$forceUpdate(); // Force Vue to re-render
              }
            }, 2000 - messageAge);
          }
        }
      });
      
      // Mark client messages as read if support has responded
      this.markClientMessagesAsRead();
    },

    getMessageTimestamp(msg) {
      if (msg.ts) {
        if (typeof msg.ts === 'object' && msg.ts.$date) {
          return new Date(msg.ts.$date).getTime();
        }
        return typeof msg.ts === 'number' ? msg.ts : parseInt(msg.ts);
      }
      if (msg._updatedAt && msg._updatedAt.$date) {
        return new Date(msg._updatedAt.$date).getTime();
      }
      return Date.now();
    },

    formatTimestampForAPI(timestamp) {
      // Convert timestamp to ISO string format for API
      if (!timestamp) return '';
      if (typeof timestamp === 'string') {
        // If already a string, validate it's ISO format
        try {
          new Date(timestamp).toISOString();
          return timestamp;
        } catch (e) {
          return '';
        }
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toISOString();
      }
      return '';
    },

    scrollToBottom() {
      const messagesArea = this.$refs.messagesArea;
      if (messagesArea) {
        messagesArea.scrollTop = messagesArea.scrollHeight;
      }
    },

    handleQuickReply(messageText) {
      // Set the message text and send it
      this.textMessage = messageText;
      this.sendMessage(messageText);
      
      // Auto-hide quick replies after sending (optional - can be toggled)
      // Uncomment the line below if you want to auto-hide after sending
      // this.showQuickReplies = false;
    },

    toggleQuickReplies() {
      this.showQuickReplies = !this.showQuickReplies;
    },

    updateContextReplies(newMessages) {
      // Find the last support message (not from user)
      const supportMessages = newMessages
        .map(msg => this.normalizeMessage(msg))
        .filter(msg => !msg.is_mine)
        .sort((a, b) => b.ts - a.ts);

      if (supportMessages.length > 0) {
        const lastSupportMsg = supportMessages[0];
        this.lastSupportMessage = lastSupportMsg;
        
        // Generate context-based suggestions based on support message content
        this.contextReplies = this.generateContextReplies(lastSupportMsg.contents);
      } else {
        this.contextReplies = [];
      }
    },

    generateContextReplies(supportMessageText) {
      if (!supportMessageText) return [];

      const messageLower = supportMessageText.toLowerCase();
      const contextReplies = [];

      // Context-based reply suggestions
      // If support mentions booking/session, suggest booking-related replies
      if (messageLower.includes('جلسه') || messageLower.includes('رزرو') || messageLower.includes('booking')) {
        contextReplies.push({
          text: 'چگونه می‌توانم جلسه رزرو کنم؟',
          icon: 'mdi-calendar-clock',
          color: '#6B7FD7',
          category: 'booking'
        });
      }

      // If support mentions payment, suggest payment-related replies
      if (messageLower.includes('پرداخت') || messageLower.includes('payment') || messageLower.includes('فاکتور')) {
        contextReplies.push({
          text: 'سوال درباره پرداخت دارم',
          icon: 'mdi-credit-card-outline',
          color: '#4CAF50',
          category: 'payment'
        });
      }

      // If support mentions technical issue, suggest technical replies
      if (messageLower.includes('فنی') || messageLower.includes('technical') || messageLower.includes('مشکل')) {
        contextReplies.push({
          text: 'مشکل فنی دارم',
          icon: 'mdi-tools',
          color: '#FF9800',
          category: 'technical'
        });
      }

      // If support mentions therapist, suggest therapist-related replies
      if (messageLower.includes('تراپیست') || messageLower.includes('therapist') || messageLower.includes('درمانگر')) {
        contextReplies.push({
          text: 'چگونه می‌توانم تراپیست خود را تغییر دهم؟',
          icon: 'mdi-account-switch',
          color: '#6B7FD7',
          category: 'therapist'
        });
      }

      // Limit to 2-3 context replies to avoid clutter
      return contextReplies.slice(0, 3);
    },

    // TODO: Unread tracking - get lastSeenTs from localStorage
    getLastSeenTs() {
      try {
        const stored = localStorage.getItem(this.lastSeenTsStorageKey);
        return stored ? parseInt(stored, 10) : null;
      } catch (error) {
        console.error('Error reading lastSeenTs from localStorage:', error);
        return null;
      }
    },

    // TODO: Unread tracking - save lastSeenTs to localStorage
    setLastSeenTs(timestamp) {
      try {
        localStorage.setItem(this.lastSeenTsStorageKey, timestamp.toString());
      } catch (error) {
        console.error('Error saving lastSeenTs to localStorage:', error);
      }
    },

    // TODO: Unread tracking - update lastSeenTs to current time when route is active
    updateLastSeenTs() {
      const now = Date.now();
      this.setLastSeenTs(now);
    },

    async refreshChat() {
      if (this.localLoader || !this.rid) return;
      
      this.localLoader = true;
      try {
        await this.loadMessages({ initial: true });
        this.calculateAverageResponseTime();
      } catch (error) {
        console.error('Error refreshing chat:', error);
      } finally {
        this.localLoader = false;
      }
    },

    handleSearch(results) {
      this.searchResults = results;
      // Scroll to first search result if any
      if (results.length > 0) {
        this.$nextTick(() => {
          const firstResult = results[0];
          const messageElement = document.querySelector(`[data-message-id="${firstResult.id}"]`);
          if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            messageElement.classList.add('search-highlight');
            setTimeout(() => {
              messageElement.classList.remove('search-highlight');
            }, 2000);
          }
        });
      }
    },

    handleClearChat() {
      // Clear local messages
      this.messages = [];
      this.textMessage = null;
      this.searchResults = [];
      this.contextReplies = [];
      this.lastSupportMessage = null;
      
      // TODO: Call API to clear chat history on server
      // await this.requesting('clientChat', 'clear', {});
      
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    handleDownloadTranscript() {
      if (!this.normalizedMessages || this.normalizedMessages.length === 0) {
        // Show notification that there are no messages
        return;
      }

      // Generate transcript text
      let transcript = 'گفتگوی پشتیبانی سیمیا روم\n';
      transcript += 'تاریخ: ' + moment().format('jYYYY/jMM/jDD HH:mm') + '\n';
      transcript += '='.repeat(50) + '\n\n';

      this.normalizedMessages.forEach(msg => {
        const sender = msg.is_mine ? 'شما' : 'پشتیبانی';
        transcript += `[${msg.date}] ${sender}:\n`;
        transcript += msg.contents + '\n\n';
      });

      // Create and download file
      const blob = new Blob([transcript], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat-transcript-${moment().format('YYYY-MM-DD-HHmm')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    calculateAverageResponseTime() {
      if (!this.normalizedMessages || this.normalizedMessages.length < 2) {
        this.averageResponseTime = null;
        return;
      }

      const responseTimes = [];
      let lastUserMessageTime = null;

      // Sort messages by timestamp
      const sortedMessages = [...this.normalizedMessages].sort((a, b) => a.ts - b.ts);

      sortedMessages.forEach(msg => {
        if (msg.is_mine) {
          lastUserMessageTime = msg.ts;
        } else if (lastUserMessageTime !== null) {
          const responseTime = msg.ts - lastUserMessageTime;
          // Convert to minutes
          const responseTimeMinutes = Math.round(responseTime / (1000 * 60));
          if (responseTimeMinutes > 0 && responseTimeMinutes < 1440) { // Less than 24 hours
            responseTimes.push(responseTimeMinutes);
          }
          lastUserMessageTime = null;
        }
      });

      if (responseTimes.length > 0) {
        const avgMinutes = Math.round(
          responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
        );
        this.averageResponseTime = `میانگین پاسخ: ${avgMinutes} دقیقه`;
      } else {
        this.averageResponseTime = null;
      }
    },

    handleBookmark({ messageId, isBookmarked }) {
      if (isBookmarked) {
        if (!this.bookmarkedMessages.includes(messageId)) {
          this.bookmarkedMessages.push(messageId);
        }
      } else {
        const index = this.bookmarkedMessages.indexOf(messageId);
        if (index > -1) {
          this.bookmarkedMessages.splice(index, 1);
        }
      }
      
      // TODO: Save bookmarks to localStorage or API
      try {
        localStorage.setItem('chat_bookmarks', JSON.stringify(this.bookmarkedMessages));
      } catch (error) {
        console.error('Error saving bookmarks:', error);
      }
    },

    isRecentMessage(timestamp) {
      if (!timestamp) return false;
      const fifteenMinutesAgo = Date.now() - (15 * 60 * 1000);
      return timestamp > fifteenMinutesAgo;
    },

    handleEditMessage({ messageId, content }) {
      // Set the message content to input for editing
      this.textMessage = content;
      
      // TODO: Mark message as being edited and update on send
      // For now, just populate the input field
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    async handleDeleteMessage({ messageId }) {
      try {
        // TODO: Call API to delete message
        // await this.requesting('clientChat', 'delete', {}, { messageId });
        
        // Remove from local messages
        const index = this.messages.findIndex(m => {
          const msgId = m.id || m._id;
          return msgId === messageId;
        });
        
        if (index > -1) {
          this.messages.splice(index, 1);
        }
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    },

    showToast(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      this.toastVisible = true;
    },

    closeToast() {
      this.toastVisible = false;
    },

    vibrate() {
      // Haptic feedback on mobile devices
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    },

    updateConnectionStatus(status) {
      this.connectionStatus = status;
    },

    async handleReconnect() {
      // Prevent multiple simultaneous reconnection attempts
      if (this.isReconnecting) {
        return;
      }

      this.isReconnecting = true;
      this.updateConnectionStatus('connecting');
      
      try {
        // Attempt to reconnect
        const resp = await this.requesting('clientChat', 'thread');
        if (resp && resp.data && resp.data.rid) {
          this.rid = resp.data.rid;
          this.setAccountManagerFromResponse(resp.data);
          // Only reload messages if we don't have any or they're stale
          if (this.messages.length === 0) {
            await this.loadMessages({ initial: true });
          }
          this.updateConnectionStatus('connected');
          this.showToast('اتصال برقرار شد', 'success');
        }
      } catch (error) {
        console.error('Reconnection failed:', error);
        this.updateConnectionStatus('disconnected');
        this.showToast('اتصال برقرار نشد', 'error');
      } finally {
        this.isReconnecting = false;
      }
    },

    checkConnection() {
      // Monitor connection status - don't trigger loadMessages here
      if (navigator.onLine) {
        if (this.connectionStatus === 'offline') {
          this.updateConnectionStatus('connecting');
          this.handleReconnect();
        } else if (this.connectionStatus === 'disconnected') {
          // Try to reconnect
          this.handleReconnect();
        }
      } else {
        this.updateConnectionStatus('offline');
      }
    },

    handleMessagesAreaKeydown(event) {
      // Keyboard navigation for messages area
      if (event.key === 'End' || (event.key === 'ArrowDown' && event.ctrlKey)) {
        event.preventDefault();
        this.scrollToBottom();
      } else if (event.key === 'Home' || (event.key === 'ArrowUp' && event.ctrlKey)) {
        event.preventDefault();
        const messagesArea = this.$refs.messagesArea;
        if (messagesArea) {
          messagesArea.scrollTop = 0;
        }
      }
    },

    announceNewMessage(message) {
      // Screen reader announcement for new messages
      const announcement = message.is_mine 
        ? `پیام شما ارسال شد: ${message.contents}`
        : `پیام جدید از پشتیبانی: ${message.contents}`;
      
      // Create temporary announcement element
      const announcementEl = document.createElement('div');
      announcementEl.setAttribute('role', 'status');
      announcementEl.setAttribute('aria-live', 'polite');
      announcementEl.setAttribute('aria-atomic', 'true');
      announcementEl.className = 'sr-only';
      announcementEl.textContent = announcement;
      document.body.appendChild(announcementEl);
      
      setTimeout(() => {
        document.body.removeChild(announcementEl);
      }, 1000);
    },


    showPushNotification(message) {
      // Request notification permission if not already granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('پیام جدید از پشتیبانی', {
          body: message.contents.length > 100 
            ? message.contents.substring(0, 100) + '...' 
            : message.contents,
          icon: '/logo/Logo.svg',
          badge: '/logo/Logo.svg',
          tag: 'chat-message',
          requireInteraction: false
        });
      } else if ('Notification' in window && Notification.permission === 'default') {
        // Request permission (only once)
        Notification.requestPermission();
      }
    },

    async handleRetryMessage({ messageId }) {
      // Find the failed message
      const failedMsg = this.messages.find(m => {
        const msgId = m.id || m._id;
        return msgId === messageId && m._failed;
      });

      if (!failedMsg || !failedMsg._retryData) return;

      // Update status to sending
      failedMsg.status = 'sending';
      failedMsg._failed = false;

      try {
        const { text, file } = failedMsg._retryData;
        let payload = { text: text || '' };
        let requestHeaders = {};

        if (file) {
          const formData = new FormData();
          formData.append('text', text || '');
          formData.append('file', file);
          payload = formData;
        }

        const resp = await this.requesting('clientChat', 'send', {}, payload, requestHeaders);

        // Remove from messages and reload
        const index = this.messages.findIndex(m => {
          const msgId = m.id || m._id;
          return msgId === messageId;
        });
        if (index > -1) {
          this.messages.splice(index, 1);
        }

        if (resp && resp.data) {
          await this.loadMessages({ initial: false });
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error('Retry failed:', error);
        failedMsg.status = 'failed';
        failedMsg._failed = true;
        this.showToast('ارسال مجدد ناموفق بود', 'error');
      }
    },
  },
  async mounted() {
    // Load bookmarked messages from localStorage
    try {
      const stored = localStorage.getItem('chat_bookmarks');
      if (stored) {
        this.bookmarkedMessages = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }

    try {
      this.localLoader = true;
      const resp = await this.requesting('clientChat', 'thread');
      
      if (resp && resp.data && resp.data.rid) {
        this.rid = resp.data.rid;
        this.setAccountManagerFromResponse(resp.data);
        await this.loadMessages({ initial: true });
      }
    } catch (error) {
      console.error('Error loading thread:', error);
    } finally {
      this.localLoader = false;
    }

    // Set up polling for messages
    // Clear any existing interval first
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    // Poll every 5 seconds
    this.pollingInterval = setInterval(() => {
      if (this.rid && !this.isLoadingMessages) {
        this.loadMessages({ initial: false });
        this.checkSupportTyping();
      }
    }, 5000); // Poll every 5 seconds

    // Check support typing status periodically
    this.typingCheckInterval = setInterval(() => {
      this.checkSupportTyping();
    }, 2000);

    // Monitor connection status
    this.checkConnection();
    const handleOnline = () => this.checkConnection();
    const handleOffline = () => this.updateConnectionStatus('offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Store handlers for cleanup
    this._onlineHandler = handleOnline;
    this._offlineHandler = handleOffline;

    // Connection status check interval
    this.connectionCheckInterval = setInterval(() => {
      this.checkConnection();
    }, 10000);

    // TODO: Unread tracking - update lastSeenTs when component is mounted (route is active)
    this.updateLastSeenTs();
  },
  // TODO: Unread tracking - update lastSeenTs when route becomes active (e.g., user navigates back to this route)
  activated() {
    this.updateLastSeenTs();
  },
  watch: {
    accountManager() {
      this.avatarLoadError = false;
    },
    '$route'(to) {
      if (to.name === 'support-chat') {
        this.updateLastSeenTs();
      }
    },
    normalizedMessages: {
      handler(newMessages) {
        if (newMessages && newMessages.length > 0) {
          const lastSupportMsg = newMessages
            .filter(msg => !msg.is_mine)
            .sort((a, b) => b.ts - a.ts)[0];
          
          if (lastSupportMsg && lastSupportMsg.contents) {
            this.contextReplies = this.generateContextReplies(lastSupportMsg.contents);
          }
        }
      },
      deep: true,
      immediate: false
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    if (this.typingCheckInterval) {
      clearInterval(this.typingCheckInterval);
      this.typingCheckInterval = null;
    }
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = null;
    }
    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler);
    }
    if (this._offlineHandler) {
      window.removeEventListener('offline', this._offlineHandler);
    }
    // Cleanup: revoke all blob URLs to prevent memory leaks
    Object.values(this.fileBlobUrlById).forEach(blobUrl => {
      if (blobUrl && typeof blobUrl === 'string') {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    });
    this.fileBlobUrlById = {};
    this.fileLoadingById = {};
    this.fileErrorById = {};
  },
};
</script>

<style scoped>
.chat-container {
  padding-top: 5px;
  /* padding: clamp(12px, 2.2vw, 28px); */
  /* background: linear-gradient(135deg, #F5F7FA 0%, #E8EDFF 100%); */
  /* min-height: 100vh; */
  /* min-height: 100dvh; */
}

.chat-wrapper {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background: #FFFFFF;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(107, 127, 215, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 0;
}

/* Message row spacing: clear gap when sender changes, tight gap for same-sender group */
.message-row {
  margin-bottom: var(--message-gap-sender-change, 14px);
}

.message-row--grouped {
  margin-bottom: var(--message-gap-same-sender, 5px);
}

.message-row:last-child {
  margin-bottom: 0;
}

.file-message-row {
  display: flex;
  width: 100%;
  justify-content: flex-start;
  margin: 0;
  direction: rtl;
}

.file-message-row--mine {
  justify-content: flex-end;
}

/* File message: stacks image/video/audio + caption + card + download + timestamp */
.file-message {
  width: fit-content;
  min-width: 0;
  max-width: min(300px, 85vw);
  background: #F5F5F5;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  padding: var(--bubble-padding, 14px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  flex-direction: column;
  gap: 10px;
  direction: rtl;
  text-align: right;
  color: #1A1A1A;
  overflow: hidden;
}

.file-message--mine {
  background: rgba(91, 124, 250, 0.12);
  border-color: rgba(91, 124, 250, 0.25);
  color: #1A1A1A;
}

.file-message__caption {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
  color: inherit;
  min-width: 0;
}

.file-message__caption--below {
  margin-top: 10px;
  margin-bottom: 0;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.file-message--mine .file-message__caption--below {
  border-top-color: rgba(91, 124, 250, 0.2);
}

.file-message__link {
  display: inline-block;
}

.file-message__caption .message-link {
  color: #4CAF50 !important;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
  word-break: break-all;
}

.file-message__caption .message-link:hover {
  color: #45A049 !important;
  text-decoration: underline;
}

.file-message--mine .file-message__caption .message-link {
  color: #81C784 !important;
  text-decoration: underline;
}

.file-message--mine .file-message__caption .message-link:hover {
  color: #A5D6A7 !important;
  text-decoration: underline;
}

.file-message__image {
  display: block;
  max-width: 100%;
  width: 100%;
  height: auto;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
}

.file-message--mine .file-message__image {
  background: rgba(255, 255, 255, 0.12);
}

.file-message__download {
  display: inline-block;
  color: inherit;
  text-decoration: underline;
  word-break: break-word;
}

.file-message__timestamp {
  margin-top: 6px;
  padding-bottom: var(--bubble-timestamp-bottom, 6px);
  font-size: 11px;
  font-weight: 400;
  color: #787878;
  text-align: end;
  direction: rtl;
  transition: color 0.2s ease;
}

.file-message__hint {
  margin-top: 8px;
  font-size: 12px;
  color: #fff;
}

.file-message__error {
  margin-top: 6px;
  font-size: 12px;
}

.file-message__actions {
  margin-top: 8px;
}

/* Custom audio player: fits bubble, minimal, no overflow */
.file-message__audio {
  margin-top: 6px;
  width: 100%;
  max-width: min(320px, 75vw);
  min-width: 0;
}

.file-message__audio audio {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.audio-player {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.file-message--mine .audio-player {
  background: rgba(255, 255, 255, 0.5);
  border-color: rgba(91, 124, 250, 0.15);
}

.audio-player__btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border: none;
  border-radius: 50%;
  background: #5B7CFA;
  color: #FFFFFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.audio-player__btn:hover {
  background: #4A6BE8;
  box-shadow: 0 2px 6px rgba(91, 124, 250, 0.3);
}

.audio-player__btn:focus {
  outline: none;
}

.audio-player__btn:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
}

.audio-player__progress-wrap {
  flex: 1;
  min-width: 0;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  cursor: pointer;
  overflow: hidden;
  transition: background 0.2s ease;
}

.file-message--mine .audio-player__progress-wrap {
  background: rgba(255, 255, 255, 0.4);
}

.audio-player__progress-wrap:hover {
  background: rgba(0, 0, 0, 0.15);
}

.file-message--mine .audio-player__progress-wrap:hover {
  background: rgba(255, 255, 255, 0.5);
}

.audio-player__progress {
  height: 100%;
  background: #5B7CFA;
  border-radius: 3px;
  transition: width 0.15s ease;
}

.audio-player__time {
  font-size: 12px;
  font-weight: 500;
  color: #666666;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 4ch;
}

.file-message--mine .audio-player__time {
  color: rgba(255, 255, 255, 0.9);
}

.audio-player__time-sep {
  opacity: 0.7;
}

.audio-player__volume {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #757575;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;
}

.audio-player__volume:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #424242;
}

.file-message--mine .audio-player__volume {
  color: rgba(255, 255, 255, 0.85);
}

.file-message--mine .audio-player__volume:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

.audio-player__volume:focus {
  outline: none;
}

.audio-player__volume:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
}

/* Attachment card – borders #E0E0E0, text primary/muted; long filenames truncate */
.file-attachment-card {
  width: 100%;
  max-width: min(300px, 100%);
  min-width: 0;
  padding: var(--bubble-padding, 14px);
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  direction: rtl;
  overflow: hidden;
}

.file-message--mine .file-attachment-card {
  background: #FFFFFF;
  border-color: #E0E0E0;
}

.file-attachment-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.file-attachment-card__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.file-attachment-card__name {
  font-size: 14px;
  font-weight: 500;
  color: #1A1A1A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  word-break: break-all;
}

.file-attachment-card__size {
  font-size: 12px;
  font-weight: 400;
  color: #666666;
}

.file-attachment-card__icon {
  flex-shrink: 0;
  color: #5B7CFA !important;
}

.file-attachment-card__actions {
  margin-top: 0;
}

.file-attachment-card__download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #5B7CFA;
  background: transparent;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.file-attachment-card__download:hover:not(:disabled) {
  background: rgba(91, 124, 250, 0.08);
  border-color: #5B7CFA;
  box-shadow: 0 1px 3px rgba(91, 124, 250, 0.15);
}

.file-attachment-card__download:focus {
  outline: none;
}

.file-attachment-card__download:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.file-attachment-card__download .v-icon {
  margin-left: 4px;
  margin-right: 0;
  color: inherit !important;
}

.file-attachment-card__download:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(91, 124, 250, 0.25);
}

/* Minimal card for image/video (name + size + download only, no icon) */
.file-attachment-card--minimal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.file-attachment-card--minimal .file-attachment-card__info {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}

.file-attachment-card--minimal .file-attachment-card__download {
  width: auto;
  flex-shrink: 0;
}

.file-message__image-wrapper {
  position: relative;
}

.file-message__image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 12px;
}

.file-message__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: #5B7CFA;
  font-size: 14px;
  font-weight: 400;
  min-height: 150px;
}

.file-message__error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: #F44336;
  font-size: 14px;
  min-height: 150px;
  text-align: center;
}

.file-message__video {
  margin-top: 8px;
  width: 100%;
  max-width: min(420px, 75vw);
}

.file-message__video-player {
  width: 100%;
  max-height: 400px;
  border-radius: 12px;
  outline: none;
}

.file-message__filename {
  flex: 1;
  word-break: break-word;
  color: #374151;
  font-weight: 500;
  font-size: 14px;
}

.file-message--mine .file-message__filename {
  color: #1f2937;
}

/* Chat header – support agent header; clear boundary above message list */
.chat-header {
  background: #6B7FF2;
  padding: 12px 16px;
  color: #FFFFFF;
  position: relative;
  z-index: 10;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.chat-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  z-index: 1;
  direction: rtl;
}

.chat-header__back {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  color: #FFFFFF;
  flex-shrink: 0;
}

.chat-header__back:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.3);
}

.chat-header__back:focus {
  outline: none;
}

.chat-header__back:focus-visible {
  outline: 2px solid #FFFFFF;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #6B7FF2;
}

.chat-header__main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  direction: rtl;
}

.chat-header__info {
  flex: 1;
  min-width: 0;
  text-align: right;
}

.chat-header__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 2px 0;
  color: #FFFFFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
}

.chat-header__subtitle {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
  margin: 0;
  opacity: 0.88;
  color: #FFFFFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-header__avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.chat-header__avatar {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.35);
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.chat-header__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-header__avatar-initials {
  font-size: 17px;
  font-weight: 600;
  color: #FFFFFF;
  line-height: 1;
  user-select: none;
}

/* Online/offline indicator dot on avatar */
.chat-header__status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #9E9E9E;
  border: 2px solid #6B7FF2;
  box-sizing: content-box;
}

.chat-header__status--online {
  background: #4CAF50;
}

.chat-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* Legacy action/back classes for ChatControls and any refs */
.header-action-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #FFFFFF;
}

.header-action-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.3);
}

.header-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.header-back-btn {
  display: none;
}

/* Messages Container - calm, minimal support chat */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  background: #FAFAFA;
  position: relative;
  scroll-behavior: smooth;
  direction: rtl;
}

.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #F5F5F5;
  border-radius: 10px;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #D1D1D1;
  border-radius: 10px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #BDBDBD;
}

.messages-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

/* Message bubble spacing constants (used by bubbles and rows) */
.messages-list {
  --bubble-padding: 14px;
  --bubble-timestamp-bottom: 6px;
  --message-gap-same-sender: 5px;
  --message-gap-sender-change: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-top: 8px;
}

.history-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #5B7CFA;
  font-size: 13px;
  direction: rtl;
}

.message-item {
  animation: messageFadeIn 0.3s ease-out;
}

.message-item--failed .file-message {
  border: 1px solid #f44336;
  background: rgba(244, 67, 54, 0.08);
}

.message-item--failed .file-message__actions .v-btn {
  border-color: #f44336 !important;
  color: #f44336 !important;
}

.file-message--mine .file-message__actions .v-btn {
  border-color: #5B7CFA !important;
  color: #5B7CFA !important;
  background: #FFFFFF !important;
}

.file-message--mine .file-message__actions .v-btn .v-icon {
  color: #5B7CFA !important;
}

.message-item--failed .file-message__error {
  color: #f44336;
}

.message-item.search-highlight {
  background: rgba(255, 235, 59, 0.3);
  border-radius: 8px;
  padding: 4px;
  margin: -4px;
  animation: highlightPulse 1s ease-out;
}

@keyframes highlightPulse {
  0%, 100% {
    background: rgba(255, 235, 59, 0.3);
  }
  50% {
    background: rgba(255, 235, 59, 0.6);
  }
}

@keyframes messageFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Skeleton Container */
.skeleton-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Empty State – welcome when no messages; responsive */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  direction: rtl;
  min-height: 280px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.empty-state-illustration {
  margin-bottom: 24px;
}

.empty-state-icon-wrapper {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E8EDFF 0%, #F0F4FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
}

.empty-state-title {
  font-size: 20px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0 0 12px 0;
  max-width: 100%;
  word-wrap: break-word;
}

.empty-state-text {
  font-size: 15px;
  color: #666666;
  font-weight: 400;
  margin: 0 0 24px 0;
  line-height: 1.6;
  max-width: min(400px, 100%);
  word-wrap: break-word;
}

.empty-state-hours {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #E0E0E0;
}

/* Input Area - Now handled by EnhancedMessageInput component */

/* RTL: header inner order (back, main, actions) */
[dir="rtl"] .chat-header__inner {
  flex-direction: row;
}

[dir="rtl"] .file-message-row {
  justify-content: flex-end;
}

[dir="rtl"] .file-message-row--mine {
  justify-content: flex-start;
}


/* Responsive Design */
@media (max-width: 960px) {
  .chat-container {
    padding: 16px;
  }
  
  .chat-wrapper {
    height: calc(100vh - 32px);
    border-radius: 20px;
  }
  
  .chat-header {
    padding: 12px 16px;
  }

  .chat-header__back {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .chat-header__avatar {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .chat-header__avatar-initials {
    font-size: 16px;
  }

  .chat-header__title {
    font-size: 18px;
  }

  .chat-header__subtitle {
    font-size: 12px;
  }

  .header-action-btn {
    width: 32px;
    height: 32px;
  }
  
  .messages-container {
    padding: 20px 16px;
  }

  .file-message {
    max-width: min(300px, 90vw);
  }
}

@media (max-width: 600px) {
  .chat-container {
    padding: 0;
  }
  
  .chat-wrapper {
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
  }
  
  .messages-container {
    height: calc(100dvh - 240px);
    max-height: calc(100dvh - 240px);
    padding: 12px 10px 140px;
  }

  .empty-state {
    padding: 24px 12px;
    min-height: 220px;
  }

  .empty-state-title {
    font-size: 18px;
  }

  .empty-state-text {
    font-size: 14px;
  }

  .file-message {
    max-width: min(300px, 92vw);
  }

  .chat-header__back {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .chat-header__avatar {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .chat-header__avatar-initials {
    font-size: 15px;
  }

  .chat-header__title {
    font-size: 17px;
  }

  .chat-header__subtitle {
    font-size: 12px;
  }
}

/* Mobile: input is fixed, so keep last messages above it */
@media (max-width: 767px) {
  .messages-container {
    padding-bottom: 200px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .message-item,
  .message-bubble,
  .send-button {
    animation: none;
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .chat-header {
    background: #4A5FA8;
    border: 2px solid #3A4F88;
  }
  
  .chat-input {
    border-width: 2px;
  }
  
  .send-button-active {
    background: #4A5FA8;
    border: 2px solid #3A4F88;
  }
  
  .message-bubble-user {
    border: 2px solid #4f46e5;
  }
  
  .message-bubble-support {
    border: 2px solid #d1d5db;
  }
  
  /* Enhanced focus indicators */
  *:focus {
    outline: 3px solid #FFC107;
    outline-offset: 2px;
  }
}

/* Focus indicators for keyboard navigation (Tab through elements) */
button:focus,
input:focus,
textarea:focus,
[tabindex]:focus {
  outline: none;
}

button:focus-visible,
input:focus-visible,
textarea:focus-visible,
[tabindex]:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
  border-radius: 4px;
  transition: outline-offset 0.2s ease, box-shadow 0.2s ease;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>

